/**
 * Shared product registry for the Semi360 embeds.
 *
 * `<semi360-header>` (header.ts) and `<semi360-rail>` both need the same
 * product list, Hub-readiness type and id-normalisation helpers, so they live
 * here as the single source of truth.
 */

export type HubLevel = "ready" | "wired" | "mockup" | "partial" | "none" | "ext";

export interface ProductInfo {
  id: string;
  n: string;
  s: string;
  hub: HubLevel;
  /** The product's one-line tagline, rendered by <semi360-footer>. Here
   *  because this is the single product registry: a tagline kept in each
   *  product's own footer is a tagline that says something different in each
   *  product. Optional — a product with no approved line renders none rather
   *  than borrowing another's. */
  t?: string;
  /** false = a known product that is NOT in the cross-product switcher.
   *  Xcess360 (P29) is open to everyone — sign up anywhere, no Hub plan — so
   *  it is not one of the org-gated products the switcher lists. It keeps its
   *  entry here because this registry is also how its OWN pages' header and
   *  footer find its name and tagline (productName, footer-model). */
  switcher?: boolean;
}

/** The products the cross-product switcher lists (every registry entry the
 *  switcher has not been told to leave out). */
export function switcherProducts(): ProductInfo[] {
  return PRODUCTS.filter((p) => p.switcher !== false);
}

// The suite, for the switcher.
//   `s`    — sort bucket (hero/core/free/avail), unchanged.
//   `hub`  — the product's Hub-readiness stage. A fact about the product (not
//            the viewer, which is what made the old "free"/"for you" tags
//            wrong). A LADDER, least → most complete:
//              "ext"      external / not on the shared platform
//              "none"     runs, but no Hub wiring
//              "partial"  Hub wiring in progress
//              "mockup"   integrated, but the product is still at the mockup
//                         stage — no real UI yet (design360)
//              "wired"    full integration plumbing (SSO+metering+audit+BOM)
//                         AND a real product, but not yet blessed as complete
//              "ready"    fully done — the LAST stage. Earned by being
//                         genuinely finished, not by having the wiring.
// Edit here, in one place, as products advance.
export const PRODUCTS: ProductInfo[] = [
  // Verified against the running services, not intent — a stale badge here is
  // worse than none, since this is the one place someone checks what is real.
  // Checked: every product marked "wired" answers a live Hub contract today
  // (401 to an anonymous call, which means gated and running). Last swept
  // 2026-09-14, when design360 and ecom360 moved up — the whole suite is
  // "wired" now, so a NEW entry is the only thing that should start lower.
  { id: "design", n: "Design360", s: "hero", hub: "wired", t: "Designing boards with 360° foresight" },  // SSO (JWKS) + metering + audit + /api/hub/bom-status, report fan-out
  { id: "sample", n: "Sample360", s: "core", hub: "wired", t: "Sampling parts with 360° follow-through" },   // SSO + BOM import
  { id: "bom", n: "Optimize360", s: "core", hub: "wired", t: "Optimising BOMs with 360° insight" },         // + /api/hub/part-usage
  { id: "xref", n: "Xref360", s: "core", hub: "wired", t: "Cross-referencing parts with 360° confidence" },       // + /api/hub/alternates, metered
  { id: "comp", n: "Comp360", s: "free", hub: "wired", t: "Checking boards with 360° coverage" },       // reads Core360 + xref360 + rfq360
  { id: "xcess", n: "Xcess360", s: "free", hub: "wired", t: "Trading surplus with 360° trust", switcher: false },      // + /api/hub/public/surplus · open to everyone, not in the switcher (P29)
  { id: "rfq", n: "RFQ360", s: "avail", hub: "wired", t: "Sourcing RFQs with 360° precision" },        // + /api/hub/part-sourcing
  { id: "qbuy", n: "Qbuy360", s: "avail", hub: "wired", t: "Buying parts with 360° clarity" },  // SSO + BOM import + /api/hub/bom-status
  // ECom360 has no approved tagline yet; the footer renders none rather than inventing one.
  { id: "ecom", n: "ECom360", s: "avail", hub: "wired" }, // Hub SSO (JWKS) + entitlement gate + /api/hub/sami-suggestions
  // Chat360 is dropped as a switchable product (P19): it has no screen
  // identity of its own — it is the embedded widget that ships WITH
  // ECom360, never something a person opens on its own. Its activity and
  // tickets still read as STORE_ASSISTANT_NAME below; there is just no
  // catalogue entry to switch to any more.
];

/** What the Hub calls Chat360's assistant on screen. "Sami" is the Hub
 *  copilot and nothing else; Chat360 is the store assistant that ships with
 *  ECom360, so a Chat360 act or ticket is labelled with this, never "Sami". */
export const STORE_ASSISTANT_NAME = "ECom360\u2019s store assistant";

/** Normalise "design360" | "Design360" | "design" → "design". */
export function normProduct(raw: string | null): string {
  if (!raw) return "";
  return raw.toLowerCase().replace(/360$/, "");
}

/** Display name for a product id/attribute value; "" when unknown. */
export function productName(raw: string | null): string {
  const id = normProduct(raw);
  return PRODUCTS.find((p) => p.id === id)?.n ?? "";
}

/**
 * Infer the Hub base URL from where an embed script was loaded.
 *
 * Shared by <semi360-header> and <semi360-rail> (each served as one script
 * tag from the Hub) so the two can't drift onto different fallback rules:
 *   1. the loading <script>'s own src (`document.currentScript`, else the
 *      first `<script>` whose src contains `scriptMatch` — a page can load
 *      several embed scripts at once) — exact whenever it's known;
 *   2. failing that, the zone the *page itself* is on: a product on
 *      `<name>.dev.semi360.ai` belongs to the dev Hub, on `<name>.semi360.ai`
 *      to the production Hub — one bundle stays correct on both zones even
 *      when NEXT_PUBLIC_HUB_BASE_URL wasn't baked in at build time;
 *   3. otherwise the caller's own built-in default (`fallback`).
 */
export function detectHubBase(scriptMatch: string, fallback: string): string {
  try {
    const cur = (document.currentScript as HTMLScriptElement | null)?.src;
    const src =
      cur ||
      Array.from(document.querySelectorAll("script"))
        .map((s) => (s as HTMLScriptElement).src)
        .find((s) => s.includes(scriptMatch));
    if (src) return new URL(src).origin;
  } catch {
    /* ignore */
  }
  const zone = window.location.hostname.match(/(?:^|\.)((?:dev\.)?semi360\.ai)$/);
  if (zone) return `https://hub.${zone[1]}`;
  return fallback;
}
